import { useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { disablePush, enablePush, isIos, refreshPush, type PushChannel } from '../lib/push';
import { publishPushState, usePushStatus } from '../lib/push-status';
import { InstallButton } from './InstallButton';
import { GESPERRT_HINWEIS, pushPromptVerborgen, verbirgPushPrompt } from './PushPrompt';
import { Badge, Button, Card, CardBody, SectionTitle, useToast } from './ui';

const TEST_ENDPOINTS: Record<PushChannel, string> = {
  account: '/api/push/test',
  link: '/api/public/sports-access/push-test',
};

/**
 * Push auf diesem Geraet ein- und ausschalten.
 *
 * Push gilt je Geraet und Browser - deshalb steht hier bewusst "dieses
 * Geraet". Auf dem iPhone geht es nur aus der App auf dem Home-Bildschirm.
 */
export function PushCard({ channel, compact = false }: { channel: PushChannel; compact?: boolean }): JSX.Element | null {
  const toast = useToast();
  const { state } = usePushStatus();
  const [busy, setBusy] = useState(false);
  const an = state === 'on';

  useEffect(() => {
    if (an) void refreshPush(channel).catch(() => undefined);
  }, [channel, an]);

  const run = async (action: () => Promise<void>): Promise<void> => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      toast.show(error instanceof ApiError || error instanceof Error ? error.message : 'Nicht möglich', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (state === null) return null;
  // Auf der Link-Seite nur zeigen, wo man etwas tun kann - sonst waere es Ballast.
  if (compact && (state === 'unsupported' || state === 'denied')) return null;
  // Solange die Einladung oben steht, waere dieselbe Schaltfläche hier doppelt.
  if (compact && (state === 'off' || state === 'needs-install') && !pushPromptVerborgen(channel)) return null;

  const title = compact ? 'Benachrichtigungen aufs Handy' : 'Push-Benachrichtigungen';
  const subtitle = compact
    ? 'Neue Umfragen und offene Rückmeldungen direkt als Nachricht'
    : 'Meldungen direkt aufs Handy oder den Computer – gilt für dieses Gerät';

  return (
    <Card>
      <CardBody className="space-y-3">
        <SectionTitle
          title={title}
          subtitle={subtitle}
          action={state === 'on' ? <Badge tone="success">Eingeschaltet</Badge> : undefined}
        />

        {state === 'unsupported' && (
          <p className="text-sm text-slate-600">Dieser Browser unterstützt keine Push-Benachrichtigungen.</p>
        )}

        {state === 'needs-install' && (
          <div className="space-y-1 text-sm text-slate-600">
            <p>
              Auf {isIos() ? 'dem iPhone und iPad' : 'diesem Gerät'} geht Push nur über die App auf dem
              Home-Bildschirm. Einmal ablegen, dann dort Push einschalten.
            </p>
            <InstallButton channel={channel} />
            <p className="hint">Nötig ist iOS 16.4 oder neuer.</p>
          </div>
        )}

        {state === 'denied' && (
          <div className="space-y-1 text-sm text-slate-600">
            <p>Benachrichtigungen sind für diese Seite blockiert.</p>
            <p>{GESPERRT_HINWEIS}</p>
          </div>
        )}

        {(state === 'off' || state === 'on') && <InstallButton channel={channel} variant="secondary" onlyDirect />}

        {state === 'off' && (
          <Button
            loading={busy}
            onClick={() => void run(async () => {
              const next = await enablePush(channel);
              publishPushState(next);
              if (next === 'on') toast.show('Push ist eingeschaltet');
            })}
          >
            Push einschalten
          </Button>
        )}

        {state === 'on' && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              loading={busy}
              onClick={() => void run(async () => {
                const { sent } = await api.post<{ sent: number }>(TEST_ENDPOINTS[channel]);
                toast.show(sent > 0 ? 'Testnachricht ist unterwegs' : 'Kein Gerät erreichbar – bitte Push aus- und wieder einschalten', sent > 0 ? 'success' : 'error');
              })}
            >
              Testnachricht senden
            </Button>
            <Button
              variant="ghost"
              loading={busy}
              onClick={() => void run(async () => {
                await disablePush(channel);
                publishPushState('off');
                verbirgPushPrompt(channel);
                toast.show('Push ist ausgeschaltet');
              })}
            >
              Ausschalten
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
